import { useCallback, useMemo, useRef } from 'react';
import posthog from 'posthog-js';

export type AIChatAnalytics = ReturnType<typeof useAIChatAnalytics>;

export function useAIChatAnalytics() {
  const openedAtRef = useRef<number | null>(null);

  const track = useCallback((event: string, properties?: Record<string, unknown>) => {
    posthog?.capture(event, properties);
  }, []);

  const trackChatOpened = useCallback(
    (properties: { source: string; page_url: string; resumed_conversation: boolean }) => {
      openedAtRef.current = Date.now();
      track('ai chat opened', properties);
    },
    [track],
  );

  const trackChatClosed = useCallback(
    (properties: { source: string; conversation_id: string | null }) => {
      const durationSeconds = openedAtRef.current
        ? Math.round((Date.now() - openedAtRef.current) / 1000)
        : null;
      openedAtRef.current = null;
      track('ai chat closed', { ...properties, duration_seconds: durationSeconds });
    },
    [track],
  );

  const trackMessageSent = useCallback(
    (properties: {
      source: string;
      conversation_id: string;
      message_length: number;
      is_first_message: boolean;
    }) => {
      track('ai chat message sent', properties);
    },
    [track],
  );

  const trackConversationCreated = useCallback(
    (properties: { source: string; conversation_id: string; page_url: string }) => {
      track('ai chat conversation created', properties);
    },
    [track],
  );

  const trackFeedbackGiven = useCallback(
    (properties: { source: string; conversation_id: string; message_id: string }) => {
      track('ai chat feedback given', properties);
    },
    [track],
  );

  const trackResponseCopied = useCallback(
    (properties: { source: string; conversation_id: string; message_id: string }) => {
      track('ai chat response copied', properties);
    },
    [track],
  );

  const trackLinkClicked = useCallback(
    (properties: {
      source: string;
      conversation_id: string;
      link_url: string;
      link_text: string;
    }) => {
      track('ai chat link clicked', properties);
    },
    [track],
  );

  const trackConversationSelected = useCallback(
    (properties: { source: string; conversation_id: string }) => {
      track('ai chat conversation selected', properties);
    },
    [track],
  );

  const trackNewConversationClicked = useCallback(
    (properties: { source: string }) => {
      track('ai chat new conversation clicked', properties);
    },
    [track],
  );

  const trackSuggestionClicked = useCallback(
    (properties: {
      source: string;
      suggestion_text: string;
      suggestion_type: 'customer' | 'generic';
    }) => {
      track('ai chat suggestion clicked', properties);
    },
    [track],
  );

  const trackStreamStarted = useCallback(
    (properties: { source: string; conversation_id: string }) => {
      track('chat_stream_started', properties);
    },
    [track],
  );

  const trackStreamFirstChunk = useCallback(
    (properties: { source: string; conversation_id: string; ttfb_ms?: number }) => {
      track('chat_stream_first_chunk', properties);
    },
    [track],
  );

  const trackStreamCompleted = useCallback(
    (properties: { source: string; conversation_id: string; total_ms?: number }) => {
      track('chat_stream_completed', properties);
    },
    [track],
  );

  return useMemo(
    () => ({
      trackChatOpened,
      trackChatClosed,
      trackMessageSent,
      trackConversationCreated,
      trackConversationSelected,
      trackNewConversationClicked,
      trackSuggestionClicked,
      trackFeedbackGiven,
      trackResponseCopied,
      trackLinkClicked,
      trackStreamStarted,
      trackStreamFirstChunk,
      trackStreamCompleted,
    }),
    [
      trackChatOpened,
      trackChatClosed,
      trackMessageSent,
      trackConversationCreated,
      trackConversationSelected,
      trackNewConversationClicked,
      trackSuggestionClicked,
      trackFeedbackGiven,
      trackResponseCopied,
      trackLinkClicked,
      trackStreamStarted,
      trackStreamFirstChunk,
      trackStreamCompleted,
    ],
  );
}
